import { useEffect, useState } from "react";

export interface SettingsSectionRef {
  id: string;
  label: string;
}

/**
 * Sticky chip index for the single-scroll Settings page (variant F). Each chip
 * scrolls to its section and the one currently in view highlights, so an owner
 * jumps to a group instead of reading a wall of switches. Horizontal-scrolls on
 * a phone so it never widens the page.
 *
 * Scroll-spy uses IntersectionObserver; jsdom has none, so we guard for it and
 * the click path (scrollIntoView + active state) works without it.
 */
const SettingsChipIndex = ({ sections }: { sections: SettingsSectionRef[] }) => {
  const [active, setActive] = useState<string>(sections[0]?.id ?? "");

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) setActive(visible[0].target.id);
      },
      // Bias toward the section whose heading has just cleared the chip bar.
      { rootMargin: "-88px 0px -55% 0px", threshold: 0 },
    );

    for (const section of sections) {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [sections]);

  const goTo = (id: string) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      aria-label="Settings sections"
      className="sticky top-0 z-20 border-b border-border bg-background py-2"
    >
      <div className="flex gap-2 overflow-x-auto">
        {sections.map((section) => {
          const isActive = active === section.id;
          return (
            <button
              key={section.id}
              type="button"
              onClick={() => goTo(section.id)}
              aria-current={isActive ? "true" : undefined}
              className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors ${
                isActive
                  ? "border-action-primary bg-action-primary text-action-primary-foreground"
                  : "border-border bg-card text-muted-foreground hover:bg-muted"
              }`}
            >
              {section.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default SettingsChipIndex;
